import { useDispatch, useSelector } from 'react-redux'
import endpoints from '../_endpoints'
import { usePost } from '../_hooks/usePost'
import { useFetch } from '../_hooks/useFetch'
import { useDelete } from '../_hooks/useDelete'
import {
  addWorkspace,
  rearrangeWorkspace,
  removeWorkspace,
  setActiveWorkspace,
  updateWorkspace
} from '../_redux/actions/workspaceActions'
import { toast } from 'react-toastify'

export const useWorkspaceService = () => {
  const dispatch = useDispatch()
  const [, updateWorkspaceData] = usePost()
  const [, fetchWorkspaces] = useFetch()
  const [, deleteWorkspaceData] = useDelete()
  const userProfile = useSelector((state) => state.profileReducer.profile)

  const workspaces = useSelector((state) => state.workspaceReducer.workspaces)

  // Utility function to remove SAS tokens from URLs
  const removeSasToken = (url) => {
    if (!url || typeof url !== 'string') return url
    const sasTokenIndex = url.indexOf('?skoid')
    return sasTokenIndex !== -1 ? url.substring(0, sasTokenIndex) : url
  }

  // Recursively clean SAS tokens from workspace configuration
  const cleanWorkspaceConfig = (obj) => {
    if (!obj) return obj

    if (Array.isArray(obj)) {
      return obj.map((item) => cleanWorkspaceConfig(item))
    }

    if (typeof obj === 'object') {
      const cleanedObj = {}
      for (const [key, value] of Object.entries(obj)) {
        if (key === 'icon' && typeof value === 'string') {
          cleanedObj[key] = removeSasToken(value)
        } else if (typeof value === 'object' || Array.isArray(value)) {
          cleanedObj[key] = cleanWorkspaceConfig(value)
        } else {
          cleanedObj[key] = value
        }
      }
      return cleanedObj
    }

    return obj
  }

  const updateWorkspaceInfo = async (request, sid) => {
    // Clean SAS tokens from the request before storing
    const cleanedRequest = cleanWorkspaceConfig(request)

    try {
      const response = await updateWorkspaceData({
        url: endpoints.workspace.saveWorkspaceConfig,
        data: {
          configKey: `${userProfile?.sid || sid}-workspace`,
          configValue: JSON.stringify(cleanedRequest)
        }
      })
      if (response.status === 'success') {
        return response
      } else {
        toast.error('An unexpected error occurred while persisting your data')
      }
    } catch (error) {
      console.error(`An unexpected error occurred: ${error}`)
    }
  }

  const addWorkspaceToList = (newWorkspace) => {
    const updatedWorkspaceObj = {
      ...workspaces,
      workspaceList: [...workspaces.workspaceList, newWorkspace],
      activeWorkspace: newWorkspace?.id
    }
    updateWorkspaceInfo(updatedWorkspaceObj)
    dispatch(addWorkspace(newWorkspace))
  }

  const updateWorkspaceList = (updatedWorkspace, id) => {
    const updatedWorkspaces = workspaces.workspaceList.map((workspaceData) => {
      return workspaceData.id === id ? { ...workspaceData, ...updatedWorkspace } : workspaceData
    })
    const updatedWorkspaceObj = {
      ...workspaces,
      workspaceList: updatedWorkspaces
    }
    updateWorkspaceInfo(updatedWorkspaceObj)
    // Clean SAS tokens from the workspace before dispatching to Redux
    const cleanedWorkspace = cleanWorkspaceConfig(updatedWorkspace)
    dispatch(updateWorkspace(cleanedWorkspace))
  }

  const removeFromWorkspaceList = (workspaceToRemove) => {
    // Match the redux entry by id first (either the client-generated id or the stamped
    // serverWorkspaceId). Older entries created before serverWorkspaceId was stamped have
    // no id to match on, so fall back to matching by name in that case only.
    const matchedWorkspace =
      workspaces.workspaceList.find(
        (item) =>
          item.id === workspaceToRemove.id || item.serverWorkspaceId === workspaceToRemove.id
      ) ||
      workspaces.workspaceList.find(
        (item) =>
          !item.serverWorkspaceId &&
          (item.name || '').trim().toLowerCase() ===
            (workspaceToRemove.name || '').trim().toLowerCase()
      ) ||
      workspaceToRemove

    const updatedWorkspaces = workspaces.workspaceList.filter(
      (item) => item.id !== matchedWorkspace.id
    )
    const updatedWorkspaceObj = {
      ...workspaces,
      workspaceList: updatedWorkspaces,
      activeWorkspace:
        matchedWorkspace.id === workspaces.activeWorkspace
          ? updatedWorkspaces.length
            ? updatedWorkspaces[updatedWorkspaces.length - 1].id
            : null
          : workspaces.activeWorkspace
    }
    //updateWorkspaceInfo(updatedWorkspaceObj)
    dispatch(removeWorkspace(matchedWorkspace))
  }

  const rearrangeWorkspaceList = (newWorkspaces) => {
    const updatedWorkspaceObj = {
      ...workspaces,
      workspaceList: newWorkspaces
    }
    updateWorkspaceInfo(updatedWorkspaceObj)
    dispatch(rearrangeWorkspace(newWorkspaces))
  }

  const updateActiveWorkspace = async (newActiveWorkspace) => {
    const updatedWorkspaceObj = {
      ...workspaces,
      activeWorkspace: newActiveWorkspace
    }
    updateWorkspaceInfo(updatedWorkspaceObj)
    dispatch(setActiveWorkspace(newActiveWorkspace))
  }

  const setUpNewWorkspace = async (workspaceObj, sid) => {
    await updateWorkspaceInfo(workspaceObj, sid)
  }

  const createWorkspace = async (payload) => {
    try {
      const response = await updateWorkspaceData({
        url: endpoints.workspace.create,
        data: payload
      })
      if (response && response.status !== 'error' && !(response instanceof Error)) {
        return response
      }
      toast.error('An unexpected error occurred while creating the workspace')
    } catch (error) {
      console.error(`An unexpected error occurred: ${error}`)
    }
  }

  // Fetch workspaces from the server, optionally filtered by privacy ('Personal' | 'Team')
  // and, for Team workspaces, by a specific teamId.
  const getWorkspaces = async (privacy, teamId) => {
    try {
      const params = new URLSearchParams()
      if (privacy) params.append('privacy', privacy)
      if (teamId) params.append('teamId', teamId)
      const queryString = params.toString()
      const url = queryString
        ? `${endpoints.workspace.getWorkspaces}?${queryString}`
        : endpoints.workspace.getWorkspaces
      const response = await fetchWorkspaces(url)
      return Array.isArray(response) ? response : []
    } catch (error) {
      console.error(`An unexpected error occurred: ${error}`)
      return []
    }
  }

  // Fetch the system/Centrali workspace templates from GET /api/workspaces/templates.
  const getWorkspaceTemplates = async () => {
    try {
      const response = await fetchWorkspaces(endpoints.workspace.getTemplates)
      return Array.isArray(response) ? response : []
    } catch (error) {
      console.error(`An unexpected error occurred: ${error}`)
      return []
    }
  }

  // Fetch a single workspace (with items) from the server via GET /api/workspaces/{id}.
  const getWorkspaceById = async (id) => {
    try {
      const url = endpoints.workspace.getById.replace(':id', id)
      const response = await fetchWorkspaces(url)
      return response && !(response instanceof Error) ? response : null
    } catch (error) {
      console.error(`An unexpected error occurred: ${error}`)
      return null
    }
  }

  // Update a workspace server-side via PUT /api/workspaces/{id}.
  const updateWorkspaceById = async (id, payload) => {
    try {
      const response = await updateWorkspaceData({
        url: endpoints.workspace.update.replace(':id', id),
        data: payload,
        method: 'put'
      })
      if (response && response.status !== 'error' && !(response instanceof Error)) {
        return response
      }
      toast.error('An unexpected error occurred while updating the workspace')
    } catch (error) {
      console.error(`An unexpected error occurred: ${error}`)
    }
  }

  // Delete a workspace server-side via DELETE /api/workspaces/{id}.
  const deleteWorkspaceById = async (id) => {
    try {
      const response = await deleteWorkspaceData({
        url: endpoints.workspace.delete.replace(':id', id),
        type: 'json'
      })
      if (response && !(response instanceof Error) && (response.ok || response.status === 204)) {
        return response
      }
      toast.error('An unexpected error occurred while deleting the workspace')
    } catch (error) {
      console.error(`An unexpected error occurred: ${error}`)
    }
  }

  return {
    addWorkspaceToList,
    updateWorkspaceList,
    removeFromWorkspaceList,
    rearrangeWorkspaceList,
    updateActiveWorkspace,
    setUpNewWorkspace,
    createWorkspace,
    getWorkspaces,
    getWorkspaceTemplates,
    getWorkspaceById,
    updateWorkspaceById,
    deleteWorkspaceById
  }
}
